import { Boss } from './boss-base';
import { getTexture, makeSprite } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';
import {
  BREATHE_AMP, BREATHE_SPEED, CLONE_COUNT, CLONE_RADIUS, ENRAGE_FIRE_MUL, ENRAGE_SPEED,
  FAN_ONE, FAN_THREE, FAN_TIER, ORBIT_R, ORBIT_R_ENRAGED, ORBIT_SPEED, PHASE, QUARTET,
  RING_INTERVAL, RING_SWELL, RING_WARN, SHOT, VOLLEY_INTERVAL, VOLLEY_WARN, WARN_TINT,
  type Clone,
} from './quartet-parts';

export { QUARTET } from './quartet-parts';

/** C6 Quartet — formation clones with phase pairs (quartet-parts.ts). */
export class QuartetBoss extends Boss {
  private clones: Clone[] = [];
  private time = 0;
  private spin = 0;
  private volleyTimer = VOLLEY_INTERVAL * 0.7;
  private ringTimer: number = RING_INTERVAL;
  /** Fire-rate multiplier this step — converts timers to seconds left */
  private fireRate = 1;
  /** Solid pair: clones with index parity === solidPair take hits */
  private solidPair = 0;
  private phaseTimer: number = PHASE.swap;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(QUARTET, level, x, y, homeX, homeY);
    this.allowReinforce = false; // the formation IS the gimmick
    this.body.visible = false; // fully custom rendering

    for (let i = 0; i < CLONE_COUNT; i++) {
      const sprite = makeSprite('boss/quartet/clone');
      this.view.addChild(sprite);
      const angle = (Math.PI * 2 * i) / CLONE_COUNT;
      this.clones.push({
        alive: true, hp: this.maxHp / CLONE_COUNT, angle,
        x: homeX + Math.cos(angle) * ORBIT_R,
        y: homeY + Math.sin(angle) * ORBIT_R,
        prevX: homeX, prevY: homeY, sprite,
      });
    }
    this.runners = this.makeRunners([], true); // tier evolve layers (A3)
  }

  /** Each clone owns a quarter — scaled WITH the power-aware maxHp (X1). */
  protected override scaleParts(mul: number): void {
    for (const c of this.clones) c.hp *= mul; // unrounded: parts sum to maxHp
    this.syncHp();
  }

  private get enraged(): boolean {
    return this.liveClones().length <= CLONE_COUNT / 2;
  }

  private liveClones(): Clone[] {
    return this.clones.filter((c) => c.alive);
  }

  private get aliveCount(): number {
    let n = 0;
    for (const c of this.clones) if (c.alive) n++;
    return n;
  }

  /** Phased ghosts are see-through: shots and blasts pass through them. */
  private solid(c: Clone): boolean {
    if (!c.alive) return false;
    if (this.aliveCount < PHASE.minAlive) return true;
    return this.clones.indexOf(c) % 2 === this.solidPair;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.time += dt;
    this.hitFlash -= dt;
    const speed = this.levelSpeedMul * (this.enraged ? ENRAGE_SPEED : 1);
    this.spin += ORBIT_SPEED * speed * dt;

    const baseR = this.enraged ? ORBIT_R_ENRAGED : ORBIT_R;
    const r = baseR + Math.sin(this.time * BREATHE_SPEED) * BREATHE_AMP;
    for (const clone of this.liveClones()) {
      clone.prevX = clone.x;
      clone.prevY = clone.y;
      const a = clone.angle + this.spin;
      clone.x = this.homeX + Math.cos(a) * r;
      clone.y = this.homeY + Math.sin(a) * r;
    }
    // Phase pairs swap; a pair with no living clone hands over at once
    this.phaseTimer -= dt;
    const pairAlive = this.clones.some((c, i) => c.alive && i % 2 === this.solidPair);
    if (this.phaseTimer <= 0 || !pairAlive) {
      this.phaseTimer = PHASE.swap;
      this.solidPair = 1 - this.solidPair;
    }
    // Boss reference point (auto-aim, homing): nearest SOLID clone
    let best = Infinity;
    for (const clone of this.clones) {
      if (!this.solid(clone)) continue;
      const d = Math.hypot(clone.x - px, clone.y - py);
      if (d < best) {
        best = d;
        this.x = clone.x;
        this.y = clone.y;
      }
    }

    this.fire(dt, px, py, bullets);
    this.fireRunners(dt, px, py, bullets, telegraphs);
  }

  private fire(dt: number, px: number, py: number, bullets: BulletSystem): void {
    // Tier tempo × enrage (and the enraged formation's own haste)
    this.fireRate = this.intensity * (this.enraged ? ENRAGE_FIRE_MUL * ENRAGE_SPEED : 1);
    // Synchronized crossfire: every clone shoots the player at ONCE —
    // standing still means getting pinched from the quarters
    this.volleyTimer -= dt * this.fireRate;
    if (this.volleyTimer <= 0) {
      this.volleyTimer = VOLLEY_INTERVAL;
      const offs = this.enraged || this.tier >= FAN_TIER ? FAN_THREE : FAN_ONE;
      for (const clone of this.liveClones()) {
        const base = Math.atan2(py - clone.y, px - clone.x);
        for (const off of offs) {
          bullets.spawn({
            x: clone.x, y: clone.y,
            dirX: Math.cos(base + off), dirY: Math.sin(base + off),
            speed: SHOT.volley * this.bulletSpeedMul, life: 4.5, radius: 10, damage: 1,
            friendly: false, texKey: 'bullet/enemy/shard', rotates: false,
          });
        }
      }
    }
    // Closing ring: each clone sprays its quarter arc inward
    this.ringTimer -= dt * this.fireRate;
    if (this.ringTimer <= 0) {
      this.ringTimer = RING_INTERVAL;
      for (const clone of this.liveClones()) {
        const inward = Math.atan2(this.homeY - clone.y, this.homeX - clone.x);
        for (let k = -2; k <= 2; k++) {
          const a = inward + k * 0.3;
          bullets.spawn({
            x: clone.x, y: clone.y, dirX: Math.cos(a), dirY: Math.sin(a),
            speed: SHOT.ring * this.bulletSpeedMul, life: 6, radius: 10, damage: 2,
            friendly: false, texKey: 'bullet/enemy/ring', rotates: false,
          });
        }
      }
    }
  }

  // ---- Collision: four bodies, each with its OWN health (docs/12 L1) ----

  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    let hits = 0;
    let dealt = 0;
    for (const clone of this.clones) {
      if (!this.solid(clone)) continue;
      hits += bullets.collideCircle(true, clone.x, clone.y, CLONE_RADIUS, (dmg) => {
        if (!clone.alive) return 0;
        const dealtHere = Math.min(clone.hp, dmg);
        clone.hp -= dmg;
        dealt += dealtHere;
        this.hitFlash = 0.08;
        if (clone.hp <= 0) this.killClone(clone);
        return dealtHere; // this clone's own loss
      });
    }
    this.syncHp();
    return { hits, dealt };
  }

  /** Explosions hurt the clone INSIDE the blast (X5). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.dead) return 0;
    const target = this.cloneInBlast(x, y, radius);
    if (!target) return 0;
    this.markAoe(target.x, target.y, CLONE_RADIUS);
    const dealt = Math.min(target.hp, damage);
    target.hp -= damage;
    this.hitFlash = 0.08;
    if (target.hp <= 0) this.killClone(target);
    this.syncHp();
    return dealt;
  }

  override takeDamage(amount: number): boolean {
    this.applyAoe(this.x, this.y, Infinity, amount);
    return this.dead;
  }

  private cloneInBlast(x: number, y: number, radius: number): Clone | null {
    let best: Clone | null = null;
    let bestD = radius + CLONE_RADIUS;
    for (const c of this.clones) {
      if (!this.solid(c)) continue;
      const d = Math.hypot(c.x - x, c.y - y);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    return best;
  }

  private killClone(clone: Clone): void {
    if (!clone.alive) return;
    clone.alive = false;
    clone.sprite.visible = false;
    this.events?.burst(clone.x, clone.y, BOSS_FEEDBACK.quartetClone);
  }

  private syncHp(): void {
    this.hp = Math.max(
      0,
      this.clones.reduce((sum, c) => sum + (c.alive ? Math.max(0, c.hp) : 0), 0), // unrounded
    );
    if (this.liveClones().length === 0) this.dead = true;
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    return this.cloneInBlast(x, y, radius) !== null;
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    return this.clones.some(
      (c) => this.solid(c) && Math.hypot(c.x - px, c.y - py) < CLONE_RADIUS + playerRadius,
    );
  }

  override render(alpha: number): void {
    this.view.position.set(0, 0);
    // Wind-up cues (X13): seconds left until each volley at the current rate
    const volleyWarn = this.volleyTimer / this.fireRate < VOLLEY_WARN;
    const ringWarn = this.ringTimer / this.fireRate < RING_WARN;
    const blink = Math.sin(this.time * 40) > 0;
    for (const clone of this.liveClones()) {
      clone.sprite.position.set(
        clone.prevX + (clone.x - clone.prevX) * alpha,
        clone.prevY + (clone.y - clone.prevY) * alpha,
      );
      // Noses track the formation's travel direction (tangent)
      clone.sprite.rotation = clone.angle + this.spin + Math.PI / 2;
      clone.sprite.texture = getTexture('boss/quartet/clone', this.enraged ? 1 : 0);
      const punch = this.hitFlash > 0 ? 1.06 : 1;
      clone.sprite.scale.set(punch * (ringWarn ? RING_SWELL : 1));
      clone.sprite.tint = (volleyWarn && blink) || ringWarn ? WARN_TINT : this.levelTint;
      // Phase ghosts are see-through; both pairs flicker before the swap
      const ghost = !this.solid(clone);
      const swapping = this.aliveCount >= PHASE.minAlive && this.phaseTimer < PHASE.warn && blink;
      clone.sprite.alpha = ghost !== swapping ? PHASE.ghostAlpha : 1;
      if (ghost && !swapping) clone.sprite.tint = PHASE.ghostTint;
    }
  }
}
